import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as React from 'react'

import { filterProps } from '../../design-tokens/design-utils'
import { Theme } from '../../design-tokens/theme'
import useSpacing, { UseSpacingProp, UseSpacingProps } from '../../design-tokens/useSpacing'
import { Locales, useIntl } from '../../localization/react'
import BaseLink from '../BaseLink'

import AppStoreBadgeDE from './img/app-store-de.svg'
import AppStoreBadgeEN from './img/app-store-en.svg'
import AppStoreBadgeES from './img/app-store-es.svg'
import AppStoreBadgeFR from './img/app-store-fr.svg'
import AppStoreBadgeIT from './img/app-store-it.svg'
import AppStoreBadgeNL from './img/app-store-nl.svg'
import PlayStoreBadgeDE from './img/play-store-de.svg'
import PlayStoreBadgeEN from './img/play-store-en.svg'
import PlayStoreBadgeES from './img/play-store-es.svg'
import PlayStoreBadgeFR from './img/play-store-fr.svg'
import PlayStoreBadgeIT from './img/play-store-it.svg'
import PlayStoreBadgeNL from './img/play-store-nl.svg'

type StoreBadgeTypes = 'play-store' | 'app-store'
type StoreBadgeRef = HTMLElementTagNameMap['a']

interface StoreBadgeProps extends UseSpacingProps {
  type: StoreBadgeTypes
  height?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  badge: {
    height: theme.spacing(4),
  },
}))

// TODO: So far, the `countryCode` has been coupled to the TLD.
// However, the apple website has a more fine-grained selection of regions…
// For example, Belgium is split into `befr` and `benl`,
// similarly, Switzerland is split into `chde` and `chfr`.
// So this definitely needs more logic!
// See https://www.apple.com/choose-country-region/
const getLocalizedAppleStoreHref = (countryCode?: string) =>
  `https://itunes.apple.com/${countryCode ? `${countryCode}/` : ''}app/eversport-now/id946460861`

const getLocalizedPlayStoreHref = (language: string) =>
  `https://play.google.com/store/apps/details?id=at.eversport&hl=${language}`

const getAppStore = (language: Locales, countryCode?: string): { href: string; src: string } => {
  const href = getLocalizedAppleStoreHref(countryCode)

  switch (language.toLowerCase()) {
    case 'de':
      return { href, src: AppStoreBadgeDE as string }
    case 'es':
      return { href, src: AppStoreBadgeES as string }
    case 'fr':
      return { href, src: AppStoreBadgeFR as string }
    case 'nl':
      return { href, src: AppStoreBadgeNL as string }
    case 'it':
      return { href, src: AppStoreBadgeIT as string }
    default:
      return { href, src: AppStoreBadgeEN as string }
  }
}

const getPlayStore = (language: Locales): { href: string; src: string } => {
  const href = getLocalizedPlayStoreHref(language)

  switch (language.toLowerCase()) {
    case 'de':
      return { href, src: PlayStoreBadgeDE as string }
    case 'es':
      return { href, src: PlayStoreBadgeES as string }
    case 'fr':
      return { href, src: PlayStoreBadgeFR as string }
    case 'nl':
      return { href, src: PlayStoreBadgeNL as string }
    case 'it':
      return { href, src: PlayStoreBadgeIT as string }
    default:
      return { href, src: PlayStoreBadgeEN as string }
  }
}

const StoreBadgeReference = (language: Locales): { [key in StoreBadgeTypes]: { href: string; src: string } } => ({
  'play-store': getPlayStore(language),
  'app-store': getAppStore(language),
})

// eslint-disable-next-line no-shadow
export const StoreBadge = React.forwardRef<StoreBadgeRef, StoreBadgeProps>(function StoreBadge(
  { type, ...props },
  ref,
) {
  const classes = useStyles()
  const { spacingClass } = useSpacing(props)
  const intl = useIntl()

  return (
    <BaseLink
      to={StoreBadgeReference(intl.context.locale.loaded)[type].href}
      ref={ref}
      {...filterProps(props, ...UseSpacingProp)}
      external
      blank
    >
      <img
        src={StoreBadgeReference(intl.context.locale.loaded)[type].src}
        alt={`${type}-badge`}
        className={classNames(!props.height ? classes.badge : null, spacingClass)}
        height={props.height || 'auto'}
        loading="lazy"
      />
    </BaseLink>
  )
})

export default StoreBadge
